import Link from 'next/link';
import styled from 'styled-components';
import Button from '@components/screening/primitives/Button';
import Callout from '@components/screening/primitives/Callout';
import {
  HomeRole,
  MapChange,
  MapCohort,
  MapHomeV2,
  PoolFilter,
  PoolRow,
} from 'screening/mapV2';
import { Avatar, BandChip, FilterPills, MelonSearch } from './parts';
import {
  Body,
  Card,
  CardTitle,
  Meta,
  Pill,
  QuietButton,
  Row,
  SecondaryButton,
  Stack,
  Title,
} from './tokens';

const CHANGE_TONE: Record<MapChange['kind'], [string, string]> = {
  role_changed: ['var(--tm-strong-bg)', 'var(--tm-strong-fg)'],
  new_interviews: ['var(--tm-good-bg)', 'var(--tm-good-fg)'],
  gap: ['var(--tm-stretch-bg)', 'var(--tm-stretch-fg)'],
};

const STATUS_TONE: Record<HomeRole['status']['kind'], [string, string]> = {
  ready: ['var(--tm-strong-bg)', 'var(--tm-strong-fg)'],
  new_fits: ['var(--tm-good-bg)', 'var(--tm-good-fg)'],
  sent: ['var(--tm-chip)', 'var(--tm-text-2)'],
  no_fit: ['var(--tm-stretch-bg)', 'var(--tm-stretch-fg)'],
};

const Hero = styled.section`
  background: linear-gradient(180deg, #ffffff 0%, var(--tm-page) 100%);
  padding: 72px 24px 56px;

  @media (max-width: 640px) {
    padding: 40px 16px 32px;
  }
`;

const HeroInner = styled.div`
  max-width: 760px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 18px;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
`;

const SuggestionChip = styled.button<{ $dashed?: boolean }>`
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0 14px;
  border-radius: 999px;
  border: 1px
    ${({ $dashed }) =>
      $dashed ? 'dashed var(--tm-dash)' : 'solid var(--tm-border-input)'};
  background: ${({ $dashed }) =>
    $dashed ? 'transparent' : 'var(--tm-surface)'};
  font-family: inherit;
  font-size: 14px;
  font-weight: ${({ $dashed }) => ($dashed ? 600 : 400)};
  color: ${({ $dashed }) => ($dashed ? 'var(--tm-navy)' : 'var(--tm-text-2)')};
  cursor: pointer;

  &:hover {
    border-color: var(--tm-dash);
  }
`;

export function MapHero({
  home,
  onSearch,
  onIntake,
}: {
  home: MapHomeV2;
  onSearch: (query: string) => void;
  onIntake: () => void;
}) {
  return (
    <Hero>
      <HeroInner>
        <Title $size={46}>{home.greeting}</Title>
        <Body $size={18} style={{ maxWidth: 640 }}>
          {home.briefing}
        </Body>
        <MelonSearch hero onSubmit={onSearch} />
        <Chips>
          {home.suggestions.map((chip) =>
            chip.kind === 'intake' ? (
              <SuggestionChip
                key={chip.label}
                type="button"
                $dashed
                onClick={onIntake}
              >
                {chip.label}
              </SuggestionChip>
            ) : (
              <SuggestionChip
                key={chip.label}
                type="button"
                onClick={() => onSearch(chip.query || chip.label)}
              >
                {chip.label}
              </SuggestionChip>
            )
          )}
        </Chips>
      </HeroInner>
    </Hero>
  );
}

export const CardGrid = styled.div<{ $min: number }>`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(min(${({ $min }) => $min}px, 100%), 1fr)
  );
  gap: 16px;
`;

const ActionLink = styled.a`
  font-weight: 600;
  font-size: 15px;
  margin-top: auto;
`;

export function ChangeCard({ change }: { change: MapChange }) {
  const [bg, fg] = CHANGE_TONE[change.kind];
  return (
    <Card style={{ gap: 12, padding: 22 }}>
      <Row $between>
        <Pill $bg={bg} $fg={fg} $size={12}>
          {change.label}
        </Pill>
        <Meta $size={13}>{change.at_label}</Meta>
      </Row>
      <Body $size={16} $tone="text" style={{ lineHeight: 1.5 }}>
        {change.sentence}
      </Body>
      <Link href={change.action.href} passHref legacyBehavior>
        <ActionLink>{change.action.label}</ActionLink>
      </Link>
    </Card>
  );
}

const PersonLine = styled.div<{ $last: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: ${({ $last }) =>
    $last ? '0' : '1px solid var(--tm-divider)'};
  min-width: 0;
`;

const LineText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

const Name = styled.a`
  display: block;
  font-weight: 600;
  font-size: 15px;
  color: var(--tm-text) !important;
`;

const Clip = styled.div`
  font-size: 14px;
  color: var(--tm-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export function RoleCard({ role }: { role: HomeRole }) {
  const [bg, fg] = STATUS_TONE[role.status.kind];
  return (
    <Card style={{ gap: 18 }}>
      <Row $between style={{ alignItems: 'flex-start', flexWrap: 'nowrap' }}>
        <Stack $gap={4}>
          <CardTitle>{role.title}</CardTitle>
          <Meta>{role.place_line}</Meta>
        </Stack>
        <Pill $bg={bg} $fg={fg} style={{ padding: '6px 10px' }}>
          {role.status.label}
        </Pill>
      </Row>
      {role.top.length > 0 ? (
        <Stack $gap={2}>
          {role.top.map((person, index) => (
            <PersonLine key={person.id} $last={index === role.top.length - 1}>
              <Avatar person={person} />
              <LineText>
                <Link href={person.href} passHref legacyBehavior>
                  <Name>{person.name}</Name>
                </Link>
                <Clip>{person.reason}</Clip>
              </LineText>
              <BandChip band={person.band} score={person.score} />
            </PersonLine>
          ))}
        </Stack>
      ) : (
        role.summary && <Body>{role.summary}</Body>
      )}
      <Row $between $gap={10} style={{ paddingTop: 4, marginTop: 'auto' }}>
        <Meta>{role.footer}</Meta>
        <Link href={role.href} passHref legacyBehavior>
          <a style={{ fontWeight: 600, fontSize: 15 }}>Open role</a>
        </Link>
      </Row>
    </Card>
  );
}

export type PoolView = 'everyone' | PoolFilter;

export function PoolFilters({
  counts,
  value,
  onChange,
}: {
  counts: MapHomeV2['pool_counts'];
  value: PoolView;
  onChange: (next: PoolView) => void;
}) {
  const options: Array<{ value: PoolView; label: string }> = [
    { value: 'everyone', label: `Everyone · ${counts.everyone}` },
    { value: 'ready', label: `Ready now · ${counts.ready}` },
    { value: 'no_fit', label: `No fit yet · ${counts.no_fit}` },
    { value: 'cv_only', label: `CV only · ${counts.cv_only}` },
  ];
  return (
    <FilterPills
      label="Filter the pool"
      options={options}
      value={value}
      onChange={onChange}
    />
  );
}

export function filterPool(rows: PoolRow[], view: PoolView) {
  if (view === 'everyone') return rows;
  return rows.filter((row) => row.filters.includes(view));
}

const GRID =
  'minmax(0, 2.2fr) minmax(0, 1.2fr) minmax(0, 2fr) minmax(0, 1.3fr) 48px';

const TableHead = styled.div`
  display: grid;
  grid-template-columns: ${GRID};
  align-items: center;
  column-gap: 16px;
  padding: 14px 24px;
  border-bottom: 1px solid var(--tm-divider);

  @media (max-width: 720px) {
    display: none;
  }
`;

const HeadLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--tm-muted);
`;

const TableBox = styled.div`
  background: var(--tm-surface);
  border: 1px solid var(--tm-border);
  border-radius: var(--tm-r-card);
  overflow: hidden;
`;

const RowLink = styled.a`
  display: grid;
  grid-template-columns: ${GRID};
  align-items: center;
  column-gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid var(--tm-divider);
  cursor: pointer;
  transition: background 140ms ease;

  &:last-child {
    border-bottom: 0;
  }

  &&,
  &&:hover {
    color: var(--tm-text);
    text-decoration: none;
  }

  &:hover,
  &:focus-visible {
    background: var(--tm-surface-2);
  }

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 8px;
    padding: 16px 18px;
  }
`;

const Cell = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const PoolName = styled.span`
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: var(--tm-text);
`;

const Small = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 14px;
  color: var(--tm-muted);
`;

const GroupText = styled.span`
  font-size: 15px;
  color: var(--tm-text-2);
`;

const FitTitle = styled.span`
  font-size: 15px;
  color: var(--tm-text);
`;

const Evidence = styled.span<{ $spoken: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ $spoken }) =>
    $spoken ? 'var(--tm-strong-fg)' : 'var(--tm-stretch-fg)'};
`;

const View = styled.span`
  justify-self: end;
  font-size: 14px;
  font-weight: 600;
  color: var(--tm-navy);

  @media (max-width: 720px) {
    justify-self: start;
  }
`;

const MobileOnly = styled.span`
  display: none;

  @media (max-width: 720px) {
    display: inline;
  }
`;

const Empty = styled.p`
  margin: 0;
  padding: 24px;
  font-size: 15px;
  color: var(--tm-muted);
`;

const withFrom = (href: string, from?: string) =>
  from ? `${href}${href.includes('?') ? '&' : '?'}from=${from}` : href;

export function PoolTable({
  rows,
  groupLabel,
  from,
}: {
  rows: PoolRow[];
  groupLabel: string;
  from?: string;
}) {
  return (
    <TableBox aria-label="People in your pool">
      <TableHead aria-hidden>
        <HeadLabel>Person</HeadLabel>
        <HeadLabel>{groupLabel}</HeadLabel>
        <HeadLabel>Best fit now</HeadLabel>
        <HeadLabel>Evidence</HeadLabel>
        <span />
      </TableHead>
      {rows.length === 0 && (
        <Empty>Nobody in the pool matches this filter yet.</Empty>
      )}
      {rows.map((row) => (
        <Link
          key={row.person.id}
          href={withFrom(row.person.href, from)}
          passHref
          legacyBehavior
        >
          <RowLink>
            <span>
              <PoolName>{row.person.name}</PoolName>
              <Small>{row.meta}</Small>
            </span>
            <GroupText>
              <MobileOnly>{groupLabel}: </MobileOnly>
              {row.group}
            </GroupText>
            <Cell>
              {row.best_fit ? (
                <>
                  <FitTitle>{row.best_fit.title}</FitTitle>
                  <BandChip
                    band={row.best_fit.band}
                    score={row.best_fit.score}
                    size="sm"
                  />
                </>
              ) : (
                <Small style={{ marginTop: 0 }}>No fit yet</Small>
              )}
            </Cell>
            <Evidence $spoken={row.spoken}>
              {row.spoken ? 'Talked to Melon' : 'CV only'}
            </Evidence>
            <View>View</View>
          </RowLink>
        </Link>
      ))}
    </TableBox>
  );
}

const BannerBody = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const BannerTitle = styled.strong`
  display: block;
  font-size: 15px;
  font-weight: 600;
  color: var(--screening-ink);
`;

const BannerDetail = styled.span`
  display: block;
  margin-top: 4px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--screening-muted);
`;

const WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
];

export const countWord = (count: number) => WORDS[count] || String(count);

export function CVOnlyBanner({
  info,
  canInvite,
  busy,
  onInvite,
}: {
  info: MapHomeV2['cv_only'];
  canInvite: boolean;
  busy?: boolean;
  onInvite: () => void;
}) {
  if (info.count <= 0) return null;
  return (
    <Callout>
      <BannerBody>
        <div style={{ maxWidth: 720 }}>
          <BannerTitle>{info.sentence}</BannerTitle>
          <BannerDetail>{info.detail}</BannerDetail>
        </div>
        {canInvite && (
          <Button variant="secondary" disabled={busy} onClick={onInvite}>
            {info.count === 1
              ? 'Invite them to talk to Melon'
              : `Invite all ${countWord(info.count)}`}
          </Button>
        )}
      </BannerBody>
    </Callout>
  );
}

const Track = styled.div`
  height: 8px;
  border-radius: 999px;
  background: var(--tm-divider);
  overflow: hidden;
`;

const Fill = styled.div<{ $pct: number }>`
  width: ${({ $pct }) => $pct}%;
  height: 100%;
  background: var(--tm-navy);
  border-radius: 999px;
`;

const SmallButton = styled(SecondaryButton)`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 14px;
`;

const SmallQuiet = styled(QuietButton)`
  min-height: 40px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 14px;
`;

export function CohortCard({
  cohort,
  canCopy,
  canNudge,
  canInvite,
  onCopy,
  onNudge,
  onInvite,
}: {
  cohort: MapCohort;
  canCopy: boolean;
  canNudge: boolean;
  canInvite: boolean;
  onCopy: () => void;
  onNudge: () => void;
  onInvite: () => void;
}) {
  const pct = cohort.total
    ? Math.round((cohort.talked / cohort.total) * 100)
    : 0;
  const open = cohort.status_label === 'Open';
  return (
    <Card>
      <Row $between>
        <CardTitle style={{ fontSize: 19 }}>{cohort.name}</CardTitle>
        <Pill
          $size={12}
          $bg={open ? 'var(--tm-strong-bg)' : 'var(--tm-chip)'}
          $fg={open ? 'var(--tm-strong-fg)' : 'var(--tm-text-2)'}
        >
          {cohort.status_label}
        </Pill>
      </Row>
      <Stack $gap={8}>
        <Track
          role="progressbar"
          aria-label={`${cohort.name} progress`}
          aria-valuemin={0}
          aria-valuemax={cohort.total}
          aria-valuenow={cohort.talked}
        >
          <Fill $pct={pct} />
        </Track>
        <span style={{ fontSize: 14, color: 'var(--tm-text-2)' }}>
          {cohort.progress_line}
        </span>
      </Stack>
      <Row>
        {cohort.imported
          ? canInvite &&
            cohort.not_started > 0 && (
              <SmallButton type="button" onClick={onInvite}>
                Invite to talk to Melon
              </SmallButton>
            )
          : canCopy &&
            open &&
            cohort.invite_path && (
              <SmallButton type="button" onClick={onCopy}>
                Copy invite link
              </SmallButton>
            )}
        {!cohort.imported && canNudge && open && cohort.not_started > 0 && (
          <SmallQuiet type="button" onClick={onNudge}>
            Nudge{' '}
            {cohort.not_started === 1
              ? 'one person'
              : `${cohort.not_started} people`}
          </SmallQuiet>
        )}
      </Row>
    </Card>
  );
}
